import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

// Shopify media IDs keep model details attached to the photo when galleries are reordered.
const MODEL_MEDIA_PEOPLE = {
  // Garment Washed Boxy Long Sleeve
  58878664311122: ['louis'],
  58874246398290: ['louis'],
  58878664343890: ['louis'],
  58878664278354: ['louis'],
  58878758682962: ['louis', 'molly'],
  58878871765330: ['louis'],
  // Americana Patch Tee
  58885643698514: ['louis'],
  58874251706706: ['louis'],
  58885643731282: ['louis'],
  58888103330130: ['louis', 'molly'],
  58888103264594: ['louis', 'molly'],
  // Bull Skull Tee
  58886589546834: ['louis'],
  58875382497618: ['louis'],
  58886589514066: ['louis'],
  58875382464850: ['louis'],
  58886589415762: ['louis'],
  58886589579602: ['louis', 'molly'],
  58886589448530: ['louis', 'molly'],
};

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.querySelector('[data-model-indicator-floating], [data-model-indicator-mobile]')) {
      const scrollRoot = this.closest('.page-wrapper') || window;
      scrollRoot.addEventListener('scroll', this.#updateModelIndicator, { passive: true, signal });
      window.addEventListener('resize', this.#updateModelIndicator, { signal });
      this.addEventListener(SlideshowSelectEvent.eventName, this.#handleModelSlideSelect, { signal });
      this.#updateModelIndicator();
    }
  }

  #controller = new AbortController();

  #setModelIndicatorVisible = (indicator, visible) => {
    if (!indicator) return;
    indicator.classList.toggle('is-visible', visible);
    indicator.setAttribute('aria-hidden', String(!visible));
  };

  #updateModelIndicatorForMedia = (indicator, media, eligible) => {
    if (!indicator) return;

    if (!this.hasAttribute('data-model-indicator-specific')) {
      this.#setModelIndicatorVisible(indicator, eligible);
      return;
    }

    const mediaId = media?.querySelector('[data-media-id]')?.getAttribute('data-media-id');
    const people = MODEL_MEDIA_PEOPLE[mediaId] || [];
    indicator.querySelectorAll('[data-model-person]').forEach((line) => {
      const visible = people.includes(line.getAttribute('data-model-person'));
      line.classList.toggle('is-model-line-visible', visible);
      line.setAttribute('aria-hidden', String(!visible));
    });
    this.#setModelIndicatorVisible(indicator, eligible && people.length > 0);
  };

  #updateModelIndicator = () => {
    const mobileIndicator = this.querySelector('[data-model-indicator-mobile]');
    if (mobileIndicator) {
      const index = this.slideshow?.current ?? 0;
      const slide = this.slideshow?.slides?.[index];
      this.#updateModelIndicatorForMedia(
        mobileIndicator,
        slide,
        window.innerWidth < 750 && index > 0 && !!slide?.classList.contains('product-media-container--image')
      );
    }

    const indicator = this.querySelector('[data-model-indicator-floating]');
    const grid = this.querySelector('.media-gallery__grid');
    if (!indicator || !grid || window.innerWidth < 750) {
      this.#setModelIndicatorVisible(indicator, false);
      return;
    }

    const media = [...grid.children];
    const viewportCenter = window.innerHeight / 2;
    let activeIndex = media.findIndex((item) => item.getBoundingClientRect().bottom > viewportCenter);
    if (activeIndex === -1) activeIndex = media.length - 1;

    this.#updateModelIndicatorForMedia(
      indicator,
      media[activeIndex],
      activeIndex > 0 &&
        (grid.getBoundingClientRect().bottom <= 0 ||
          !!media[activeIndex]?.classList.contains('product-media-container--image'))
    );
  };

  #handleModelSlideSelect = (event) => {
    if (event.target !== this.slideshow) return;

    const indicator = this.querySelector('[data-model-indicator-mobile]');
    this.#updateModelIndicatorForMedia(
      indicator,
      event.detail.slide,
      window.innerWidth < 750 &&
        event.detail.index > 0 &&
        !!event.detail.slide?.classList.contains('product-media-container--image')
    );
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Size selections do not change product imagery. Keep the existing gallery
    // mounted so the shopper stays on their current image without a refresh.
    if (event.detail?.optionName?.trim().toLowerCase() === 'size') return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
